import { ProgramData } from 'redux/employee/employeeTypes'
import { EmployeeTransitPass } from 'redux/transitPass/transitPassTypes'

const ENROLLED_STATUSES = new Set([
  'ACTIVE',
  'UPDATING',
  'CANCELLING',
  'PENDING',
])

export type BenefitIcon =
  | 'transit'
  | 'parking'
  | 'flex'
  | 'grh'
  | 'micromobility'
  | 'otp'
  | 'transitPass'
  | 'coupon'

export interface CommuterBenefitItem {
  id: string
  title: string
  subtitle?: string
  icon: BenefitIcon
  amountCents?: number
  ridesLeft?: number
  ridesAllowed?: number
  membershipStatus?: EmployeeTransitPass['status']
  statusLabel?: string
}

interface QtfProgramGroup {
  active?: {
    type?: string[]
    numUses?: number
    id?: string
  }
}

interface GrhProgramGroup {
  active?: {
    id?: string
    numUses?: number
  }
}

export interface CommuterBenefitInput {
  programData?: ProgramData[]
  transitPasses?: EmployeeTransitPass[]
  segmentsEnrolled?: string[]
  tagsEnrolled?: string[]
  qtfBalanceData?: {
    transitCycleBalance?: number
    parkingCycleBalance?: number
  }
  qtfPrograms?: Record<string, QtfProgramGroup> | null
  grhPrograms?: Record<string, GrhProgramGroup> | null
}

const enrolledPrograms = (programData: ProgramData[] | undefined, type: string) =>
  (programData || []).filter(
    (program) => program.type === type && ENROLLED_STATUSES.has(program.status)
  )

const amountLeft = (program: ProgramData) =>
  Math.max(0, (program.budget || 0) - (program.totalSpend || 0))

const scopeIsEnrolled = (
  scopeId: string,
  segmentsEnrolled: string[],
  tagsEnrolled: string[]
) => segmentsEnrolled.includes(scopeId) || tagsEnrolled.includes(scopeId)

const benefitName = (
  superProgramName: string | null | undefined,
  typeLabel: string
) =>
  superProgramName
    ? { title: superProgramName, subtitle: typeLabel }
    : { title: typeLabel }

const qtfTypeEnrolled = (
  input: CommuterBenefitInput,
  qtfType: 'COMMUTER_TRANSIT' | 'COMMUTER_PARKING'
) => {
  const segments = input.segmentsEnrolled || []
  const tags = input.tagsEnrolled || []
  return Object.entries(input.qtfPrograms || {}).some(([scopeId, group]) => {
    if (!scopeIsEnrolled(scopeId, segments, tags)) {
      return false
    }
    return (group.active?.type || []).includes(qtfType)
  })
}

const grhRideLimit = (
  program: ProgramData,
  grhPrograms: CommuterBenefitInput['grhPrograms']
) => {
  const scoped = grhPrograms?.[program.segmentId]?.active
  if (scoped?.numUses != null) {
    return scoped.numUses
  }
  const match = Object.values(grhPrograms || {}).find(
    (group) => group.active?.id === program.id
  )
  return match?.active?.numUses ?? null
}

export const buildCommuterBenefits = (
  input: CommuterBenefitInput
): CommuterBenefitItem[] => {
  const items: CommuterBenefitItem[] = []
  const transitBalance = input.qtfBalanceData?.transitCycleBalance || 0
  const parkingBalance = input.qtfBalanceData?.parkingCycleBalance || 0

  if (qtfTypeEnrolled(input, 'COMMUTER_TRANSIT') || transitBalance > 0) {
    items.push({
      id: 'qtf-transit',
      title: 'Transit',
      icon: 'transit',
      amountCents: transitBalance,
    })
  }

  if (qtfTypeEnrolled(input, 'COMMUTER_PARKING') || parkingBalance > 0) {
    items.push({
      id: 'qtf-parking',
      title: 'Parking',
      icon: 'parking',
      amountCents: parkingBalance,
    })
  }

  enrolledPrograms(input.programData, 'FLEX').forEach((program) => {
    items.push({
      id: program.id,
      ...benefitName(program.superProgramName, 'Flex'),
      icon: 'flex',
      amountCents: amountLeft(program),
    })
  })

  enrolledPrograms(input.programData, 'GRH').forEach((program) => {
    const allowed = grhRideLimit(program, input.grhPrograms)
    const used = program.numTransactions || 0
    items.push({
      id: program.id,
      ...benefitName(program.superProgramName, 'Guaranteed Rides'),
      icon: 'grh',
      ridesAllowed: allowed ?? undefined,
      ridesLeft: allowed == null ? undefined : Math.max(0, allowed - used),
    })
  })

  enrolledPrograms(input.programData, 'MICROMOBILITY').forEach((program) => {
    items.push({
      id: program.id,
      ...benefitName(program.superProgramName, 'Micromobility'),
      icon: 'micromobility',
      amountCents: amountLeft(program),
    })
  })

  enrolledPrograms(input.programData, 'OTP').forEach((program) => {
    items.push({
      id: program.id,
      ...benefitName(program.superProgramName, 'One Time Purchase'),
      icon: 'otp',
      amountCents: amountLeft(program),
    })
  })

  enrolledPrograms(input.programData, 'COUPON').forEach((program) => {
    items.push({
      id: program.id,
      ...benefitName(program.superProgramName, 'Coupon'),
      icon: 'coupon',
      statusLabel: program.couponCode?.trim() || '—',
    })
  })

  ;(input.transitPasses || [])
    .filter((pass) => pass.status !== 'DEACTIVATED')
    .forEach((pass) => {
      items.push({
        id: pass.id,
        title: pass.transitAgencyName || 'Transit Pass',
        icon: 'transitPass',
        membershipStatus: pass.status,
      })
    })

  return items
}
